/**
 * 請求書詳細 `/invoices/$invoiceId`。
 * ヘッダ + 明細 + 合計 + 支払い履歴。印刷を想定して 1 カラムで組む。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Banknote, Download, Printer, RefreshCw, Undo2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  DataTable,
  Input,
  KeyValue,
  PageHeader,
  Section,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  formatCount,
  formatDateTime,
  type DataTableColumn,
} from '@mail-sv/ui';
import { labelOf } from '@mail-sv/shared';
import { api, openPresigned } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { formatJpy } from '../lib/format.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { FormDialog } from '../components/dialogs.js';
import { Field } from '../components/form-fields.js';
import { DetailSkeleton, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type { InvoiceDetail } from '../types/api.js';

const routeApi = getRouteApi(RID.invoiceDetail);

/** 1 件の入金。`InvoiceDetail['payments']` の要素。 */
export type InvoicePayment = InvoiceDetail['payments'][number];

/**
 * 入金済み合計。**返金分（`refundedJpy`）を必ず引く。**
 *
 * 引かないと、返金した請求書が画面上だけ「満額入金済み」に見え続ける。
 * サーバー側の消し込みは `sum(amount - refunded_amount)`（`status = 'succeeded'`
 * のみ）で判定しているので、**同じ式でなければ画面とサーバーで残額が食い違う**
 * （運営が「入金済み」と読んだ請求書を、サーバーは `open` のままにしている）。
 *
 * 全額返金は `status` が `refunded` になり、そのとき `amount - refunded` は
 * 必ず 0 なので、除外しても合計は変わらない。部分返金は `succeeded` のまま
 * 残るので、**この行を除外してはいけない**（除外すると入金そのものが
 * 丸ごと消え、残額が入金額ぶん過大になる）。
 */
export function paidTotalOf(payments: readonly InvoicePayment[]): number {
  return payments
    .filter((p) => p.status === 'succeeded')
    .reduce((sum, p) => sum + p.amountJpy - p.refundedJpy, 0);
}

/** 残額。**過入金でも負にしない**（マイナスの請求額は請求書として成立しない）。 */
export function remainingOf(totalJpy: number, paidTotal: number): number {
  return Math.max(0, totalJpy - paidTotal);
}

/**
 * この入金に「返金」を出してよいか。
 *
 * サーバーは全額返金済みの入金を「全額返金済みです」で 400 にする。
 * 押してから断られる導線を作らないため、画面でも同じ条件で消す。
 * `refunded`（全額返金済み）を条件に残しているのは、**部分返金を重ねた
 * 結果ちょうど全額に達した入金**が `refunded` になるためで、残りが 0 なら
 * どのみち出ない。
 */
export function canRefund(payment: InvoicePayment, canWrite: boolean): boolean {
  if (!canWrite) return false;
  if (payment.amountJpy - payment.refundedJpy <= 0) return false;
  return payment.status === 'succeeded' || payment.status === 'refunded';
}

export function InvoiceDetailPage(): React.ReactElement {
  const { invoiceId } = routeApi.useParams();
  const qc = useQueryClient();
  const canWrite = usePermission(OPS_PERMISSION.billingWrite);

  const query = useQuery({
    queryKey: qk.invoice(invoiceId),
    queryFn: () => api.get<InvoiceDetail>(`/invoices/${invoiceId}`),
  });

  const invoice = query.data;
  const [creditNoteOpen, setCreditNoteOpen] = React.useState(false);
  const [voidOpen, setVoidOpen] = React.useState(false);
  const [markPaidOpen, setMarkPaidOpen] = React.useState(false);
  const [refunding, setRefunding] = React.useState<InvoiceDetail['payments'][number] | null>(null);

  const paidTotal = paidTotalOf(invoice?.payments ?? []);
  const remaining = invoice ? remainingOf(invoice.totalJpy, paidTotal) : 0;

  /*
   * PDF の再生成（`POST /invoices/:id/pdf`）。発行者情報の env を入れた直後や、
   * 生成に失敗して `pdf_object_key` が空のままの請求書を作り直すための入口。
   * 生成は worker が行うので 202 で返る（すぐには開けない）。
   */
  const regenerate = useMutation({
    mutationFn: () => api.post(`/invoices/${invoiceId}/pdf`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.invoice(invoiceId) }),
  });

  const paymentColumns: Array<DataTableColumn<InvoiceDetail['payments'][number]>> = [
    { id: 'at', header: '日時', cell: (p) => formatDateTime(p.at) },
    { id: 'amount', header: '金額', align: 'right', cell: (p) => formatJpy(p.amountJpy) },
    {
      id: 'refunded',
      header: '返金',
      align: 'right',
      // 0 を「—」にする。返金があった入金だけが視線に引っかかるようにしたい。
      cell: (p) => (p.refundedJpy > 0 ? <span className="text-warning">{formatJpy(p.refundedJpy)}</span> : '—'),
    },
    { id: 'method', header: '方法', cell: (p) => labelOf(p.method) },
    { id: 'status', header: '状態', cell: (p) => <StatusBadge status={p.status} size="sm" /> },
    {
      id: 'reconciled',
      header: '消し込み',
      hideBelow: 'md',
      cell: (p) =>
        p.reconciled ? (
          <span className="text-xs text-muted-foreground">済</span>
        ) : (
          <span className="text-xs text-warning">未</span>
        ),
    },
    { id: 'note', header: '備考', cell: (p) => <span className="text-xs text-muted-foreground">{p.note ?? '—'}</span> },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-32',
      // 全額返金済みの入金には出さない（サーバーも「全額返金済みです」で 400 にする）。
      cell: (p) =>
        canRefund(p, canWrite) ? (
          <Button variant="outline" size="sm" onClick={() => setRefunding(p)}>
            <Undo2 aria-hidden />
            返金
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title={invoice ? invoice.number : '請求書'}
        description={invoice ? invoice.organizationName : undefined}
        breadcrumbs={[{ label: '請求書', href: '/invoices' }, { label: invoice?.number ?? '…' }]}
        actions={
          <>
            <RefetchingDot active={query.isFetching && !query.isLoading} />
            {invoice ? <StatusBadge status={invoice.status} dot /> : null}
            {invoice ? (
              <>
                {/*
                  PDF は presigned URL（`{url, expiresAt}`）で返る。Blob で保存すると
                  JSON がそのまま .pdf になって開けない。生成前なら 404 が返るので、
                  そのときのために「再生成」も並べておく。
                */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void openPresigned(`/invoices/${invoice.id}/pdf`)}
                >
                  <Download aria-hidden />
                  PDF
                </Button>
                {canWrite ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => regenerate.mutate()}
                    loading={regenerate.isPending}
                  >
                    <RefreshCw aria-hidden />
                    PDF を再生成
                  </Button>
                ) : null}
                {/*
                  残額の消し込み（`POST /invoices/:id/mark-paid`）。
                  **金額を送らない**ので、いくら残っているかはサーバーが数える
                  （分割入金の 2 回目でも正しい）。細かい金額・入金日を指定したい
                  ときは請求書一覧の「入金を登録」を使う。
                */}
                {canWrite && invoice.status !== 'void' && invoice.status !== 'paid' && remaining > 0 ? (
                  <Button variant="outline" size="sm" onClick={() => setMarkPaidOpen(true)}>
                    <Banknote aria-hidden />
                    残額を消し込む
                  </Button>
                ) : null}
                {/*
                  返還請求書（赤伝）。**無効化とは別の操作**で、既に交付した
                  適格請求書を取り消さずに、返還した事実を別の証憑として残す。
                  入金済み・交付済みのものを訂正するときはこちらを使う。
                */}
                {canWrite && invoice.status !== 'void' ? (
                  <Button variant="outline" size="sm" onClick={() => setCreditNoteOpen(true)}>
                    <Undo2 aria-hidden />
                    返還請求書
                  </Button>
                ) : null}
                {/*
                  無効化（`POST /invoices/:id/void`）。**返還請求書（赤伝）とは別物**で、
                  こちらは「発行しなかったことにする」操作。交付前・未入金の
                  訂正はこちらが正しい。入金済みは API 側が 400 で弾く（返金で処理する）。
                  副作用（`usage_daily.billed_invoice_id` と
                  `subscriptions.base_fee_paid_through` の巻き戻し）まで実装済みなのに、
                  **呼ぶ画面が無く一度も使われていなかった**。
                */}
                {canWrite && invoice.status !== 'void' && invoice.status !== 'paid' ? (
                  <Button variant="outline" size="sm" className="text-destructive" onClick={() => setVoidOpen(true)}>
                    <Ban aria-hidden />
                    無効化
                  </Button>
                ) : null}
                <Button variant="outline" size="sm" onClick={() => window.print()}>
                  <Printer aria-hidden />
                  印刷
                </Button>
              </>
            ) : null}
          </>
        }
      />

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}
      {query.isLoading ? <DetailSkeleton /> : null}

      {invoice ? (
        <div className="space-y-6">
          <Section title="請求内容">
            <div className="rounded-lg border border-border bg-card p-4">
              <KeyValue
                columns={3}
                items={[
                  {
                    key: '契約組織',
                    value: (
                      <Link
                        to="/organizations/$orgId"
                        params={{ orgId: invoice.organizationId }}
                        search={{ tab: 'invoices' }}
                        className="hover:text-primary"
                      >
                        {invoice.organizationName}
                      </Link>
                    ),
                  },
                  { key: '対象期間', value: `${formatDateTime(invoice.periodStart)} 〜 ${formatDateTime(invoice.periodEnd)}` },
                  { key: '状態', value: <StatusBadge status={invoice.status} size="sm" /> },
                  { key: '発行日', value: formatDateTime(invoice.issuedAt) },
                  { key: '支払期限', value: formatDateTime(invoice.dueAt) },
                  { key: '入金日', value: formatDateTime(invoice.paidAt) },
                ]}
              />
            </div>
          </Section>

          <Section title="明細">
            <div className="overflow-x-auto rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">品目</TableHead>
                    <TableHead scope="col" className="text-right">
                      数量
                    </TableHead>
                    <TableHead scope="col" className="text-right">
                      単価
                    </TableHead>
                    <TableHead scope="col" className="text-right">
                      金額
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoice.lines.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell>{line.description}</TableCell>
                      <TableCell className="text-right tabular">{formatCount(line.quantity)}</TableCell>
                      <TableCell className="text-right tabular">{formatJpy(line.unitPriceJpy)}</TableCell>
                      <TableCell className="text-right tabular">{formatJpy(line.amountJpy)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <dl className="ml-auto mt-3 w-full max-w-xs space-y-1 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">小計</dt>
                <dd className="tabular">{formatJpy(invoice.subtotalJpy)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">消費税</dt>
                <dd className="tabular">{formatJpy(invoice.taxJpy)}</dd>
              </div>
              <div className="flex justify-between border-t border-border pt-1 text-base font-semibold">
                <dt>合計</dt>
                <dd className="tabular">{formatJpy(invoice.totalJpy)}</dd>
              </div>
              {/*
                **入金済みと残額を出す。** 出さないと、支払い履歴の表を目で
                足し算しないと「あといくら未収か」が分からない。分割入金の
                2 回目を登録するときに必ず要る数字。
              */}
              <div className="flex justify-between text-muted-foreground">
                <dt>入金済み</dt>
                <dd className="tabular">{formatJpy(paidTotal)}</dd>
              </div>
              <div className="flex justify-between font-medium">
                <dt>残額</dt>
                <dd className={remaining > 0 ? 'tabular text-warning' : 'tabular'}>{formatJpy(remaining)}</dd>
              </div>
            </dl>
          </Section>

          <Section title="支払い履歴">
            <DataTable
              columns={paymentColumns}
              rows={invoice.payments}
              rowKey={(p) => p.id}
              emptyTitle="入金の記録はありません"
            />
          </Section>
        </div>
      ) : null}

      {invoice ? (
        <>
          <CreditNoteDialog invoice={invoice} open={creditNoteOpen} onOpenChange={setCreditNoteOpen} />
          <VoidInvoiceDialog invoice={invoice} open={voidOpen} onOpenChange={setVoidOpen} />
          <MarkPaidDialog
            invoice={invoice}
            remaining={remaining}
            open={markPaidOpen}
            onOpenChange={setMarkPaidOpen}
          />
          <RefundDialog invoiceId={invoice.id} payment={refunding} onClose={() => setRefunding(null)} />
        </>
      ) : null}
    </>
  );
}

/* ---------------------------------------------------------- 残額の消し込み */

/**
 * 残額をまとめて入金として記録する（`POST /ops/invoices/:invoiceId/mark-paid`）。
 *
 * **金額を送らない。** サーバーが「総額 − これまでの入金（返金を引いた額）」を
 * 数えて 1 件の入金を作る。画面で残額を組み立てて送ると、開いている間に
 * 別の運営者が一部入金を登録した場合に過入金ができてしまう。
 * 金額・入金日を指定したいときは請求書一覧の「入金を登録」を使う
 * （そちらは同じ API に金額を明示して送る）。
 */
function MarkPaidDialog({
  invoice,
  remaining,
  open,
  onOpenChange,
}: {
  invoice: InvoiceDetail;
  remaining: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [payerName, setPayerName] = React.useState('');
  const [externalPaymentId, setExternalPaymentId] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setPayerName('');
    setExternalPaymentId('');
  }, [open]);

  const submit = useMutation({
    mutationFn: () =>
      api.post<{ paymentId: string; amount: number; invoicePaid: boolean }>(
        `/invoices/${invoice.id}/mark-paid`,
        {
          method: 'bank_transfer',
          payerName: payerName.trim() === '' ? undefined : payerName.trim(),
          externalPaymentId: externalPaymentId.trim() === '' ? undefined : externalPaymentId.trim(),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.invoice(invoice.id) });
      void qc.invalidateQueries({ queryKey: ['ops', 'invoices'] });
      void qc.invalidateQueries({ queryKey: ['ops', 'payments'] });
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`${invoice.number} の残額を消し込む`}
      description={`残額 ${formatJpy(remaining)} を銀行振込の入金として記録します。満額に達した時点で請求書が「入金済み」になり、未納による停止も解除されます。`}
      submitLabel="消し込む"
      pending={submit.isPending}
      error={submit.isError ? submit.error : undefined}
      onSubmit={() => submit.mutate()}
    >
      <Alert variant="info">
        <AlertDescription>
          金額はサーバーが数え直します（この画面を開いている間に別の入金が登録されても過入金になりません）。
          一部だけ入金された場合は、請求書一覧の「入金を登録」から金額を指定してください。
        </AlertDescription>
      </Alert>

      <Field label="振込名義" hint="通帳に記載された名義。任意ですが、後から突合するときの手掛かりになります。">
        <Input value={payerName} onChange={(e) => setPayerName(e.target.value)} />
      </Field>

      <Field label="取引 ID" hint="銀行の取引 ID。入れておくと二重登録をサーバー側で弾けます。">
        <Input
          value={externalPaymentId}
          onChange={(e) => setExternalPaymentId(e.target.value)}
          spellCheck={false}
        />
      </Field>
    </FormDialog>
  );
}

/* -------------------------------------------------------------- 無効化 */

/**
 * 請求書の無効化（`POST /ops/invoices/:invoiceId/void`）。
 *
 * **返還請求書（赤伝）と混同しないこと。**
 *   - 無効化 … 交付前・未入金の請求書を「発行しなかったこと」にする。
 *     日次集計の凍結（`usage_daily.billed_invoice_id`）と基本料の前払い終端
 *     （`subscriptions.base_fee_paid_through`）まで戻すので、**作り直せる**。
 *   - 返還請求書 … 既に交付した適格請求書は取り消さず、返還した事実を
 *     別の証憑として残す。
 *
 * 入金済みは API 側が 400 で弾く（返金で処理する）ので、ボタン自体を出さない。
 */
function VoidInvoiceDialog({
  invoice,
  open,
  onOpenChange,
}: {
  invoice: InvoiceDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const submit = useMutation({
    mutationFn: () => api.post(`/invoices/${invoice.id}/void`, { reason: reason.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.invoice(invoice.id) });
      void qc.invalidateQueries({ queryKey: ['ops', 'invoices'] });
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="請求書を無効化する"
      description={`${invoice.number} を「発行しなかったこと」にします。交付済み・入金済みのものには使えません。`}
      submitLabel="無効化する"
      pending={submit.isPending}
      error={submit.error}
      disabled={reason.trim().length === 0}
      onSubmit={() => submit.mutate()}
    >
      <Alert variant="warning">
        <AlertDescription>
          無効化すると、この請求書が凍結していた日次集計と、基本料の前払い済み終端が発行前へ戻ります。
          締め直せば同じ期間の請求書を作り直せます。<strong>既に契約者へ渡した請求書は、無効化ではなく返還請求書で訂正してください。</strong>
        </AlertDescription>
      </Alert>
      <Field label="理由" required hint="請求書のメモと監査ログに残ります（500 文字まで）。">
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="対象期間の指定を誤って発行したため（交付前・未入金）"
        />
      </Field>
    </FormDialog>
  );
}

/* ---------------------------------------------------------- 返還請求書 */

/**
 * 返還請求書（適格返還請求書）の発行要求。
 *
 * **理由は必須**（サーバーの zod が `min(1)`）。税務調査で「なぜ返還したか」を
 * 説明できない証憑を作らない。発行そのものは worker が行うので 202 で返り、
 * 一覧に出るまで少し遅れる。同じ請求書への赤伝は 1 通だけ（2 通目は 409）。
 */
function CreditNoteDialog({
  invoice,
  open,
  onOpenChange,
}: {
  invoice: InvoiceDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const submit = useMutation({
    mutationFn: () => api.post(`/invoices/${invoice.id}/credit-note`, { reason: reason.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.invoice(invoice.id) });
      void qc.invalidateQueries({ queryKey: ['ops', 'invoices'] });
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="返還請求書を発行する"
      description={`${invoice.number} に対する適格返還請求書を発行します。元の請求書は取り消しません。`}
      submitLabel="発行を要求する"
      pending={submit.isPending}
      error={submit.error}
      disabled={reason.trim().length === 0}
      onSubmit={() => submit.mutate()}
    >
      <Field label="返還の理由" required hint="請求書と監査ログに残ります。">
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="二重請求のため全額を返還（起票: SUP-2026-0031）"
        />
      </Field>
      <Alert>
        <AlertDescription>
          発行はワーカーが行うため、一覧に出るまで少し時間がかかります。同じ請求書への返還請求書は 1 通だけです。
        </AlertDescription>
      </Alert>
    </FormDialog>
  );
}

/* -------------------------------------------------------------- 返金 */

/**
 * 入金の返金。**理由は必須**（金銭を戻す操作を根拠なしに実行できるようにしない）。
 * 決済事業者（Stripe）経由の入金は「要求を積む」ところまでで、反映は worker が行う。
 */
function RefundDialog({
  invoiceId,
  payment,
  onClose,
}: {
  invoiceId: string;
  payment: InvoiceDetail['payments'][number] | null;
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const remaining = payment ? payment.amountJpy - payment.refundedJpy : 0;
  const [amount, setAmount] = React.useState('');
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (!payment) return;
    // 既定は残額の全額。分割返金もできるように編集可能にしておく。
    setAmount(String(payment.amountJpy - payment.refundedJpy));
    setReason('');
  }, [payment]);

  const submit = useMutation({
    mutationFn: () =>
      api.post(`/payments/${payment?.id ?? ''}/refund`, {
        amount: Number(amount),
        reason: reason.trim(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.invoice(invoiceId) });
      void qc.invalidateQueries({ queryKey: ['ops', 'invoices'] });
      onClose();
    },
  });

  const value = Number(amount);
  const invalid = !(value > 0) || value > remaining || reason.trim().length === 0;

  return (
    <FormDialog
      open={payment !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="入金を返金する"
      description={`返金できる残額は ${formatJpy(remaining)} です。`}
      submitLabel="返金する"
      pending={submit.isPending}
      error={submit.error}
      disabled={invalid}
      onSubmit={() => submit.mutate()}
    >
      <Field label="返金額（円）" required>
        <Input
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ''))}
          inputMode="numeric"
        />
      </Field>
      <Field label="理由" required hint="監査ログに残ります。">
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
      </Field>
      <Alert>
        <AlertDescription>
          決済事業者経由の入金は、ここでの操作は<strong>返金要求の受付</strong>までです。実際の返金は決済側の処理が終わってから反映されます。
        </AlertDescription>
      </Alert>
    </FormDialog>
  );
}
